// libs
import { History } from 'history'
import { omit } from 'lodash'
import * as React from 'react'

// interfaces / constants
import {
  MSGBOX_BUTTON_TYPE_CANCEL,
  MSGBOX_BUTTON_TYPE_DANGER
} from 'src/components/message_box/message_box'
import MessageBoxWithModal from 'src/components/message_box/message_box_with_modal'
import { CLASS_PREFIX } from 'src/constants/'
import { ITrackingProps } from 'src/high_order_components/with_tracker_props'
import { IAddress } from 'src/interfaces/address'
import { IImageObject } from 'src/interfaces/attachment'
import { ILinkPreview } from 'src/interfaces/post_ingredients'
import {
  IContentItemType
} from 'src/interfaces/posts'
import { textResources } from 'src/lang/de'
import { IEventData } from 'src/post-creation-wizard/wizards/common/interfaces'
import { getConfigValue } from 'src/utils/config/config'
import { PostCreateObjType } from 'src/utils/reporting/events_tracking/events_tracking'

// components
import Modal from 'src/components/modal/modal'
import FloatingPostCreate from 'src/containers/smart_components/floating_post_create/floating_post_create'

// helpers
import { getLabel, postSubmitTracking } from 'src/components/post/utils'
import {
  COLOR_TYPE_PRIMARY,
  COLOR_TYPE_PRIMARY_2,
  COLOR_TYPE_SECONDARY6
} from 'src/utils/color'
import { readColumnCountFromDOM } from 'src/utils/column_count'
import DateFormatter from 'src/utils/date_formatter/date_formatter'
import { PREVIEW_PATH } from 'src/constants/route_constants'
import { getCreatePathFrom, UrlUtils } from 'src/utils/url/url'

import { ActorProfilePreviewFragmentFragment, AddressFragmentFragment, EventFormat } from '@lopo-tech/lopo-api'
import { IContentItemMutations } from 'src/high_order_components/with_content_item_mutations'
import { CombinedGraphQLErrors } from '@apollo/client/errors'
import { showSnackBar } from 'src/components/snack_bar/snack_bar'
import { formatNeedsLink, formatNeedsPlace } from 'src/utils/event_format'
import { IWizardProps } from 'src/post-creation-wizard/wizards/create/create_wizard'
import { urlForServer, isUsableLink } from 'src/utils/validation'
import { ISurveyParams } from '../../../interfaces/survey'
import LegacyButton, { BUTTON_TYPE_TEXT } from '../../forms/button/button'
import './create.scss'
const PostCreationWizard = React.lazy(
  () =>
    import(
      /* webpackChunkName: "post-creation-wizard" */ 'src/post-creation-wizard/wizards/create/create_wizard'
    )
)

const cls = CLASS_PREFIX + 'post-create'
const labels = textResources.postCreate

type DefaultIconType =
  | typeof COLOR_TYPE_PRIMARY
  | typeof COLOR_TYPE_PRIMARY_2
  | typeof COLOR_TYPE_SECONDARY6;

export interface IOwnProps {
  postType?: IContentItemType;
  defaultIconType?: DefaultIconType;
  groupId?: string;
  groupName?: string;
  topicId?: string;
  open?: boolean;
  featureBlocked: boolean;
  onClose?: () => void;
}

export interface IMapRouteToProps {
  history: History;
  path: string;
  matchUrl: string;
  search: string;
}

export interface IPostCreateStoreState {
  loggedInProfileAddress: IAddress;
  profileType: ActorProfilePreviewFragmentFragment['__typename'];
}

type IPostCreateProps = IPostCreateStoreState &
  IContentItemMutations &
  IOwnProps &
  IMapRouteToProps &
  ITrackingProps<PostCreateObjType>;

interface IState {
  address?: AddressFragmentFragment;
  body: string;
  oglinkId?: string;
  columnCount: number;
  commentable: boolean;
  draftedImages: IImageObject[][];
  eventData: IEventData;
  firstStep?: string;
  isCreatePostLoading: boolean;
  linkPreviewId?: string;
  linkPreviewParams?: ILinkPreview;
  postType?: IContentItemType;
  preFilledAddress?: AddressFragmentFragment;
  savedImages: IImageObject[];
  showMessageBox: boolean;
  surveyParams?: ISurveyParams;
  title: string;
  titleImageIndex: number;
}

const initialState = {
  address:
    getConfigValue<IAddress>('featureFlags.location.defaultAddress') ||
    undefined,
  body: '',
  oglinkId: undefined,
  columnCount: 0,
  commentable: true,
  draftedImages: [],
  eventData: {
    category: undefined,
    endDate: undefined,
    endTime: undefined,
    startDate: undefined,
    startTime: undefined
  },
  firstStep: undefined,
  isCreatePostLoading: false,
  postType: undefined,
  preFilledAddress: undefined,
  savedImages: [],
  showMessageBox: false,
  title: '',
  titleImageIndex: 0
}

class PostCreate extends React.Component<IPostCreateProps, IState> {
  constructor (props: IPostCreateProps) {
    super(props)
    this.cancel = this.cancel.bind(this)
    this.cleanUpImages = this.cleanUpImages.bind(this)
    this.createArticle = this.createArticle.bind(this)
    this.createEvent = this.createEvent.bind(this)
    this.createSnapshot = this.createSnapshot.bind(this)
    this.closeMessageBox = this.closeMessageBox.bind(this)
    this.hideWizard = this.hideWizard.bind(this)
    this.onModalClose = this.onModalClose.bind(this)
    this.setAddress = this.setAddress.bind(this)
    this.setBody = this.setBody.bind(this)
    this.setCommentable = this.setCommentable.bind(this)
    this.setDraftedImages = this.setDraftedImages.bind(this)
    this.setFirstStep = this.setFirstStep.bind(this)
    this.setPostType = this.setPostType.bind(this)
    this.setSavedImages = this.setSavedImages.bind(this)
    this.setTitle = this.setTitle.bind(this)
    this.setSurveyDetails = this.setSurveyDetails.bind(this)
    this.setTitleImageIndex = this.setTitleImageIndex.bind(this)
    this.showMessageBox = this.showMessageBox.bind(this)
    this.updateEvent = this.updateEvent.bind(this)
    this.setLinkPreviewParams = this.setLinkPreviewParams.bind(this)
    this.state = {
      ...initialState,
      columnCount: readColumnCountFromDOM()
    }
  }

  public render () {
    const {
      loggedInProfileAddress,
      history,
      matchUrl,
      groupName,
      open,
      search,
      profileType,
      tracking,
      featureBlocked
    } = this.props

    const {
      address,
      body,
      commentable,
      draftedImages,
      eventData,
      firstStep,
      isCreatePostLoading,
      linkPreviewParams,
      oglinkId,
      postType,
      preFilledAddress,
      savedImages,
      showMessageBox,
      surveyParams,
      title,
      titleImageIndex
    } = this.state

    const params: Omit<IWizardProps, 'onEnd'> = {
      address,
      basePath: getCreatePathFrom(matchUrl),
      body,
      cleanUpImages: this.cleanUpImages,
      commentable,
      createArticle: this.createArticle,
      createEvent: this.createEvent,
      createSnapshot: this.createSnapshot,
      draftedImages,
      eventData,
      firstStep,
      groupName,
      history,
      label: getLabel(profileType),
      linkPreviewParams,
      loggedInProfileAddress,
      oglinkId,
      onCancel: this.showMessageBox,
      postType,
      preFilledAddress,
      profileType: this.props.profileType,
      isCreatePostLoading,
      savedImages,
      search,
      setAddress: this.setAddress,
      setBody: this.setBody,
      setCommentable: this.setCommentable,
      setDraftedImages: this.setDraftedImages,
      setFirstStep: this.setFirstStep,
      setLinkPreviewParams: this.setLinkPreviewParams,
      setPostType: this.setPostType,
      setSavedImages: this.setSavedImages,
      setSurveyDetails: this.setSurveyDetails,
      setTitle: this.setTitle,
      setTitleImageIndex: this.setTitleImageIndex,
      surveyParams,
      title,
      titleImageIndex,
      tracking,
      updateEvent: this.updateEvent
    }

    return (
      <>
        {open && (
          <Modal onClose={this.onModalClose}>
            <React.Suspense fallback={null}>
              <PostCreationWizard
                className={cls + '__post-creation-wizard'}
                onEnd={this.hideWizard}
                {...params}
              />
              <LegacyButton
                variant={BUTTON_TYPE_TEXT}
                fullWidth
                onClick={this.onModalClose}
                label={textResources.shared.cancel}
                lowerCase
              />
            </React.Suspense>
          </Modal>
        )}
        {showMessageBox && (
          <MessageBoxWithModal
            buttons={[
              {
                label: labels.closeWindow,
                onClick: this.cancel,
                type: MSGBOX_BUTTON_TYPE_DANGER
              },
              {
                label: labels.continueWritingPost,
                onClick: this.closeMessageBox,
                type: MSGBOX_BUTTON_TYPE_CANCEL
              }
            ]}
            onClose={this.closeMessageBox}
            title={labels.cancelTitle}
          >
            <p>{labels.cancelMessage}</p>
          </MessageBoxWithModal>
        )}
        <FloatingPostCreate
          postType={this.props.postType}
          featureBlocked={featureBlocked}
        />
      </>
    )
  }

  private setLinkPreviewParams (linkPreviewParams: ILinkPreview) {
    this.setState({ linkPreviewParams })
    this.setOglinkId(linkPreviewParams?.id)
  }

  private setDraftedImages (draftedImages: IImageObject[][]) {
    this.setState({ draftedImages })
  }

  private setFirstStep (id: string) {
    this.setState({ firstStep: id })
  }

  private updateEvent (newEventData: IEventData) {
    this.setState({ eventData: { ...this.state.eventData, ...newEventData } })
  }

  private setTitleImageIndex (index: number) {
    this.setState({ titleImageIndex: index })
  }

  private setSavedImages (savedImages: IImageObject[]) {
    this.setState({ savedImages })
  }

  private cleanUpImages () {
    this.setState({ draftedImages: [] })
  }

  private setAddress (address: AddressFragmentFragment) {
    this.setState({ address })
  }

  private setPostType (postType?: IContentItemType) {
    const { tracking } = this.props
    if (postType) {
      postSubmitTracking(postType, tracking.trackingObj.LABELS.START, tracking)
    }
    this.setState({ postType })
  }

  private setBody (body: string) {
    this.setState({ body })
  }

  private setOglinkId (oglinkId?: string) {
    this.setState({ oglinkId })
  }

  private setTitle (title: string) {
    this.setState({ title })
  }

  private setSurveyDetails (surveyParams: ISurveyParams) {
    this.setState({ surveyParams })
  }

  private setCommentable (commentable: boolean) {
    this.setState({ commentable })
  }

  private onModalClose () {
    this.showMessageBox()
  }

  private hideWizard () {
    const { history, matchUrl, search } = this.props
    history.push(getCreatePathFrom(matchUrl) + search)
  }

  private showMessageBox () {
    this.setState({ showMessageBox: true })
  }

  private closeMessageBox () {
    this.setState({ showMessageBox: false })
  }

  private cancel () {
    const { matchUrl, history, search, onClose } = this.props
    this.setState(omit(initialState, 'columnCount'))
    onClose?.()
    history.replace(getCreatePathFrom(matchUrl) + search)
  }

  private async createArticle () {
    const { title, body, oglinkId, savedImages, titleImageIndex, commentable } =
      this.state
    this.setState({ isCreatePostLoading: true })

    // prepare images for article creation
    const images = this.reorderImages(savedImages, titleImageIndex).map(
      (image) => image.src
    )

    // create assets for article
    const assetIds = await this.props.createAsset(images)

    // create article itself
    const res = await this.props.createArticle({
      variables: {
        input: {
          assetIds,
          topicId: this.props.topicId,
          oglinkId,
          title,
          body,
          commentable,
          placeId: this.state.address?.googlePlaceId
        }
      }
    })

    this.setState({ isCreatePostLoading: false })

    if (res.data?.createArticle?.article.slug) {
      const redirectTo = UrlUtils.detailPage(
        res.data.createArticle.article.slug,
        'Article'
      )

      setTimeout(() => {
        this.props.history.push(redirectTo)
      }, 500)
    }
  }

  // Every way an event submit can fail ends here: say what happened, stop the
  // spinner, and put the draft back in front of the person. Anything that only
  // does the first two leaves "your post is being published" standing.
  private abandonSubmit (message: string) {
    const { history, matchUrl, search } = this.props

    this.setState({ isCreatePostLoading: false })
    showSnackBar(message)
    history.replace(`${getCreatePathFrom(matchUrl)}/event${PREVIEW_PATH}${search}`)
  }

  private async createEvent () {
    const { title, body, oglinkId, savedImages, titleImageIndex, eventData } =
       this.state

    this.setState({ isCreatePostLoading: true })

    const format = eventData.format ?? EventFormat.InPerson
    const needsPlace = formatNeedsPlace(format)
    const needsLink = formatNeedsLink(format)
    const placeId = this.state.address?.googlePlaceId
    // The check runs on what was typed. Normalising first would put a scheme in
    // front of every value, and the rule that a bare word needs a dot is written
    // against a value that brought none - it would never fire again.
    //
    // What is sent is what `URL` made of it: the check accepts an umlaut domain
    // because `URL` punycodes the host, and sending the raw string instead had
    // the scalar answer "URI must be ascii only".
    const typedUrl = eventData.onlineMeetingUrl ?? ''
    const onlineMeetingUrl = urlForServer(typedUrl)

    // The format decides what is required. Saying which one is missing, rather
    // than returning without a word as this did for every case before - and
    // before the images go up, so a refused submit does not upload them once per
    // attempt.
    //
    // Each of these also leaves the submitting screen. Not because it is on
    // screen yet - measured with a MutationObserver across 168 frames, the
    // submitting step is inserted and removed within the same turn on this path
    // and never painted - but because without the `history.replace` the wizard
    // stays on the submit route, and a slower machine or a rerender is all it
    // takes for "your post is being published" to become visible.
    if (needsPlace && !placeId) {
      this.abandonSubmit(labels.addressRequired)
      return
    }

    if (needsLink && !isUsableLink(typedUrl)) {
      this.abandonSubmit(labels.onlineLinkRequired)
      return
    }

    if (!eventData.category || !eventData.startTime || !eventData.startDate) {
      this.abandonSubmit(textResources.shared.genericErrorBody)
      return
    }

    const images = this.reorderImages(savedImages, titleImageIndex).map(
      (image) => image.src
    )

    let res

    try {
      // Inside as well, although the guard above covers the empty case and the
      // date field rejects anything it cannot parse (measured: `99.99.9999`,
      // `31.02.2026` and `abc` all leave the field empty and the button
      // inactive). `toDateTimeISOString` splits what `moment` returns, so a
      // value that ever did get through would throw here - and the point of
      // this block is that nothing leaves the person on the publishing screen.
      const startTime = DateFormatter.toDateTimeISOString(
        eventData.startDate,
        eventData.startTime
      )
      const endTime =
        eventData.endDate && eventData.endTime
          ? DateFormatter.toDateTimeISOString(
            eventData.endDate,
            eventData.endTime
          )
          : undefined

      const assetIds = await this.props.createAsset(images)

      res = await this.props.createEvent({
        variables: {
          input: {
            assetIds,
            topicId: this.props.topicId,
            title,
            body,
            oglinkId,
            categoryIds: [eventData.category],
            startTime,
            endTime,
            // Only what the format asks for: an online event must not carry a
            // leftover place id, which the server would drop without saying so.
            placeId: needsPlace ? placeId : undefined,
            format,
            onlineMeetingUrl: needsLink ? onlineMeetingUrl : undefined,
            commentable: this.state.commentable
          }
        }
      })
    } catch (errors) {
      // The submit button navigates on click, so by the time the mutation
      // answers, the wizard is already showing "your post is being published".
      // A refusal used to leave it there, with the button greyed out and the
      // draft out of reach. Going back to the preview puts it in front of the
      // person again.
      //
      // What they read is our own sentence, not the server's: those are English
      // and phrased for us ("... is not a valid URL"). The original goes to the
      // console, so that a report of "something went wrong" can still be traced
      // to what was refused.
      //
      // Not `logToDevConsole`: that one is gated on `js_env.env === 'development'`,
      // and this build reports `staging` even when run from a developer's
      // machine - measured - so the wording would be lost in every environment
      // anyone actually looks at.
      if (CombinedGraphQLErrors.is(errors)) {
        // eslint-disable-next-line no-console
        console.error('createEvent refused:', errors.message)
      }

      this.abandonSubmit(textResources.shared.genericErrorBody)

      return
    }

    const slug = res.data?.createEvent?.event.slug

    if (!slug) {
      // A resolved mutation without a slug. Not reachable through the API as it
      // behaves today, but the submitting step reads the loading flag going
      // false as success and shows a tick - so the one exit that does not end
      // here would end on a checkmark for an event that does not exist.
      this.abandonSubmit(textResources.shared.genericErrorBody)

      return
    }

    this.setState({ isCreatePostLoading: false })

    const redirectTo = UrlUtils.detailPage(slug, 'Event')

    setTimeout(() => {
      this.props.history.push(redirectTo)
    }, 500)
  }

  private async createSnapshot () {
    const { body, savedImages, titleImageIndex } = this.state
    this.setState({ isCreatePostLoading: true })

    // prepare images for snapshot creation
    const images = this.reorderImages(savedImages, titleImageIndex).map(
      (image) => image.src
    )

    // create assets for snapshot
    const assetIds = await this.props.createAsset(images)

    // create snapshot itself
    const res = await this.props.createSnapshot({
      variables: {
        input: {
          assetIds,
          topicId: this.props.topicId,
          body,
          commentable: true,
          placeId: this.state.address?.googlePlaceId
        }
      }
    })

    this.setState({ isCreatePostLoading: false })

    if (res.data?.createSnapshot?.snapshot.slug) {
      const redirectTo = UrlUtils.detailPage(
        res.data.createSnapshot.snapshot.slug,
        'Snapshot'
      )

      setTimeout(() => {
        this.props.history.push(redirectTo)
      }, 500)
    }
  }

  // reorder the images array to have the title image first
  private reorderImages (
    images: IImageObject[],
    titleImageIndex?: number
  ): IImageObject[] {
    if (
      !!images.length &&
      !!titleImageIndex &&
      titleImageIndex > 0 &&
      titleImageIndex < images.length
    ) {
      return [
        images[titleImageIndex],
        ...images.filter((_val, i) => i !== titleImageIndex)
      ]
    }

    return images
  }
}

export default PostCreate
